import {
  DestroyRef,
  Directive,
  inject,
  input,
  InputSignal,
  NgZone,
  output,
  OutputEmitterRef,
  Renderer2
} from '@angular/core';
import { MIN_COLUMN_WIDTH } from './data-grid.types';

/** Live or committed width for a single column field. */
export interface DataGridColumnResizeWidth {
  /** Column field being resized. */
  readonly field: string;
  /** Width in pixels. */
  readonly width: number;
}

/**
 * Document-level column-resize drag attached to a header handle.
 *
 * Pointer listeners are attached on mousedown and always removed on mouseup
 * or directive destroy. Live width emits re-enter the zone so the table can
 * update template columns during the drag.
 */
@Directive({
  selector: '[dDataGridResizeHandle]',
  host: {
    '(mousedown)': 'onMouseDown($event)',
    '(dblclick)': 'onDoubleClick($event)'
  }
})
export class DataGridColumnResizeDirective {
  /** Column field being resized. */
  public field: InputSignal<string> = input.required<string>();

  /** Width in pixels at mousedown. */
  public startWidth: InputSignal<number> = input.required<number>();

  /** Minimum width in pixels. */
  public minWidth: InputSignal<number> = input<number>(MIN_COLUMN_WIDTH);

  /** Emits while the pointer is down and moving. */
  public liveWidthChange: OutputEmitterRef<DataGridColumnResizeWidth> = output<DataGridColumnResizeWidth>();

  /** Emits true on mousedown and false on mouseup. */
  public resizeActive: OutputEmitterRef<boolean> = output<boolean>();

  /** Emits once mousemove has fired during the drag. */
  public dragOccurred: OutputEmitterRef<void> = output<void>();

  /** Emits the final width on mouseup. */
  public widthCommit: OutputEmitterRef<DataGridColumnResizeWidth> = output<DataGridColumnResizeWidth>();

  /** Emits when the handle is double-clicked to reset width. */
  public widthReset: OutputEmitterRef<string> = output<string>();

  /** Angular Renderer2 used to attach document mousemove/mouseup listeners. */
  private readonly renderer: Renderer2 = inject(Renderer2);

  /** NgZone for running resize commits inside change detection. */
  private readonly ngZone: NgZone = inject(NgZone);

  /** Tears down in-flight document listeners if the host is destroyed mid-drag. */
  private readonly destroyRef: DestroyRef = inject(DestroyRef);

  /** Detach function for the active document mousemove listener. */
  private removeMoveListener: (() => void) | null = null;

  /** Detach function for the active document mouseup listener. */
  private removeUpListener: (() => void) | null = null;

  /** Initializes DataGridColumnResizeDirective and registers drag teardown. */
  constructor() {
    this.destroyRef.onDestroy((): void => {
      this.detachResizeListeners();
    });
  }

  /**
   * Starts a document-level resize drag.
   *
   * @param event - The mousedown event from the handle.
   */
  protected onMouseDown(event: MouseEvent): void {
    event.stopPropagation();
    event.preventDefault();
    this.detachResizeListeners();

    const field: string = this.field();
    const startX: number = event.clientX;
    const startWidth: number = this.startWidth();
    const minWidth: number = this.minWidth();

    this.resizeActive.emit(true);

    let latestWidth: number = startWidth;
    let dragged: boolean = false;

    this.ngZone.runOutsideAngular((): void => {
      this.removeMoveListener = this.renderer.listen(
        'document',
        'mousemove',
        (moveEvent: MouseEvent): void => {
          if (!dragged) {
            dragged = true;
            this.ngZone.run((): void => {
              this.dragOccurred.emit();
            });
          }
          const delta: number = moveEvent.clientX - startX;
          latestWidth = Math.max(startWidth + delta, minWidth);
          this.ngZone.run((): void => {
            this.liveWidthChange.emit({ field, width: latestWidth });
          });
        }
      );

      this.removeUpListener = this.renderer.listen('document', 'mouseup', (): void => {
        this.detachResizeListeners();
        this.ngZone.run((): void => {
          this.resizeActive.emit(false);
          this.widthCommit.emit({ field, width: latestWidth });
        });
      });
    });
  }

  /**
   * Resets the column to its definition/default width.
   *
   * @param event - The dblclick event from the handle.
   */
  protected onDoubleClick(event: MouseEvent): void {
    event.stopPropagation();
    event.preventDefault();
    this.widthReset.emit(this.field());
  }

  /** Removes document-level drag listeners if they are attached. */
  private detachResizeListeners(): void {
    if (this.removeMoveListener !== null) {
      this.removeMoveListener();
      this.removeMoveListener = null;
    }
    if (this.removeUpListener !== null) {
      this.removeUpListener();
      this.removeUpListener = null;
    }
  }
}
